import api from 'services/api';
import { REJECT, array, nullable, num, object, refuseDegraded, str } from 'validation';
import { readResponse } from 'api/shared/readResponse';

// A PROJECTION CONTRACT over `GET /recurrence_definitions` — see ../../README.md.
// The full contract is `getRecurrenceDefinitions`, which declares eighteen fields
// because the list page reads them, including every rule in `recurrences[]`. A
// picker reads three.
//
// Declaring only those is what lets it refuse a degraded answer without refusing
// on drift it does not care about: one odd `unit` in one unrelated definition used
// to take out the control. `object()` ignores the rest of the payload, so nothing
// else is parsed and nothing else can warn.
//
// It shares the route with `getRecurrenceDefinitions` today; a slim backend read
// would replace the request and leave this contract standing.

/** A recurrence definition as a picker deals in it. */
export interface RecurrenceDefinitionSummary {
  readonly recurrence_definition_id: number;
  readonly name: string;
  readonly zone_name: string;
}

// `zone_name` is nullable on the wire but a string here: an LOV option needs one,
// and a definition with no zone renders as ''. Mapped rather than declared, so the
// projection stays honest about what the wire sends.
interface RecurrenceDefinitionRow {
  readonly recurrence_definition_id: number;
  readonly name: string;
  readonly zone_name: string | null;
}

const rowSchema = object<RecurrenceDefinitionRow>({
  recurrence_definition_id: num(REJECT),
  name: str(),
  zone_name: nullable(str()),
});

interface RecurrenceDefinitionsLOVResponse {
  readonly nodes: readonly RecurrenceDefinitionRow[];
}

const responseSchema = object<RecurrenceDefinitionsLOVResponse>({
  nodes: array(rowSchema),
});

// Server pagination defaults are small — ask for a generous limit so the
// client-side Typeahead filter has every option to match against.
const LOV_LIMIT = 1000;

/**
 * Every recurrence definition the user can see, narrowed to what a picker renders.
 *
 * Collapses with `refuseDegraded`, and that is a property of this contract rather
 * than a policy imposed on an unknown consumer: a definition missing from an
 * options list is one the user cannot pick, and one they picked before no longer
 * resolves. A partial list of values is not a list of values.
 */
export const loadRecurrenceDefinitionsLOV = async (): Promise<readonly RecurrenceDefinitionSummary[]> => {
  const res = await api.get('/recurrence_definitions', { params: { limit: LOV_LIMIT } });
  const { nodes } = refuseDegraded(readResponse(res.data, { schema: responseSchema, source: 'loadRecurrenceDefinitionsLOV' }));
  return nodes.map((node) => ({
    recurrence_definition_id: node.recurrence_definition_id,
    name: node.name,
    zone_name: node.zone_name ?? '',
  }));
};
